import moment from "moment-timezone";
import { Text } from "north.js";
import React, { useMemo } from "react";
import styled from "styled-components";

import {
  Accordion,
  AccordionArrow,
  AccordionHeadingContainer,
} from "src/shared/components";

import type { GetVolunteerShiftAvailabilityQuery } from "../../shift-schedule/graphql/getVolunteerShiftAvailability.generated";
import type { GetVolunteerShiftScheduleQuery } from "../../shift-schedule/graphql/getVolunteerShiftSchedule.generated";

import ShiftCard from "./ShiftCard";

type VolunteerShift =
  GetVolunteerShiftScheduleQuery["volunteerShiftSchedule"][number];
type Availability =
  GetVolunteerShiftAvailabilityQuery["volunteerShiftAvailability"][number];

type Props = {
  shifts: VolunteerShift[];
  availability: Availability[];
  now: moment.Moment;
  onShiftClick: (shift: { shift_id: number }) => void;
};

const LiveAttendancePanel: React.FC<Props> = ({
  shifts,
  availability,
  now,
  onShiftClick,
}) => {
  const ongoing = useMemo(
    () =>
      shifts
        .filter((shift) => now.isBetween(shift.start_time, shift.end_time))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [shifts, now]
  );

  const availabilityByShift = useMemo(
    () =>
      new Map(
        availability.map((shiftAvailability) => [
          shiftAvailability.shift_id,
          shiftAvailability,
        ])
      ),
    [availability]
  );

  const totals = useMemo(() => {
    return ongoing.reduce(
      (acc, shift) => {
        const shiftAvailability = availabilityByShift.get(shift.shift_id);
        const signupCount = shiftAvailability?.signup_count ?? 0;
        const checkedInCount = shiftAvailability?.checked_in_count ?? 0;
        acc.volunteers += signupCount;
        acc.checkedIn += checkedInCount;
        acc.missing += signupCount - checkedInCount;
        acc.shifts += 1;
        return acc;
      },
      { volunteers: 0, checkedIn: 0, missing: 0, shifts: 0 }
    );
  }, [ongoing, availabilityByShift]);

  return (
    <Accordion
      isDefaultOpen
      heading={
        <Heading>
          <HeadingText>
            <Title>Live attendance</Title>
            <Subtitle>
              {totals.shifts} shifts • {totals.volunteers} volunteers •{" "}
              {totals.checkedIn} checked in
              {totals.missing > 0 ? ` • ${totals.missing} missing` : ""}
            </Subtitle>
          </HeadingText>
          <StyledAccordionArrow />
        </Heading>
      }
    >
      <Body>
        {ongoing.length === 0 ? (
          <Text type="body2">No shifts are ongoing right now.</Text>
        ) : (
          <CardsContainer>
            {ongoing.map((shift) => {
              const shiftAvailability = availabilityByShift.get(shift.shift_id);
              const signupCount = shiftAvailability?.signup_count ?? 0;
              const checkedInCount = shiftAvailability?.checked_in_count ?? 0;
              return (
                <ShiftCard
                  key={shift.shift_id}
                  shiftName={shift.name}
                  startTime={shift.start_time}
                  endTime={shift.end_time}
                  shiftLocation={shift.location || "N/A"}
                  category={shift.category_name}
                  registeredUsers={signupCount}
                  maxUsers={shift.max_volunteer_count ?? 0}
                  onClick={() => onShiftClick(shift)}
                  checkedIn={false}
                  now={now}
                  attendance={{
                    checkedIn: checkedInCount,
                    missing: signupCount - checkedInCount,
                  }}
                />
              );
            })}
          </CardsContainer>
        )}
      </Body>
    </Accordion>
  );
};

const Heading = styled(AccordionHeadingContainer)`
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: ${({ theme }) =>
    theme.globalConstants.color.brandSecondary};
  border-radius: 8px;
  border-top: 1px solid
    ${({ theme }) => theme.globalConstants.color.brandSecondary};
  border-bottom: 1px solid
    ${({ theme }) => theme.globalConstants.color.brandSecondary};
`;

const HeadingText = styled.div`
  flex-grow: 1;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.globalConstants.color.textLight};
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.globalConstants.color.textLight};
  font-size: 14px;
  margin: 4px 0 0;
`;

const StyledAccordionArrow = styled(AccordionArrow)`
  svg {
    fill: ${({ theme }) => theme.globalConstants.color.textLight};
  }
`;

const Body = styled.div`
  padding: 16px 16px 32px;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  ${({ theme }) => theme.mediaQueries.medium`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}

  ${({ theme }) => theme.mediaQueries.tablet`
    grid-template-columns: 1fr;
  `}
`;

export default LiveAttendancePanel;
